import api from '../../api/client';
import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Nav,
  Alert,
  Button,
  ProgressBar,
  Badge,
  Spinner,
  Form,
  Table,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import DataTable from "react-data-table-component";
import axios from "axios";
import config from "../../config";
import Select from "react-select";
import SlaughterWaste from "../../components/SlaughterWaste";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const AnimalAnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [summaryData, setSummaryData] = useState({});
  const [growthRates, setGrowthRates] = useState([]);
  const [weightDistribution, setWeightDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeRange, setTimeRange] = useState("30");
  const [projectionData, setProjectionData] = useState(null);
  const [projectionInput, setProjectionInput] = useState({
    tagNumber: "",
    animalType: "",
    targetWeight: "",
  });
  const [tags, setTags] = useState([]);
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const [loadingTags, setLoadingTags] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [feedConsumption, setFeedConsumption] = useState([]);
  const [feedConversionRatios, setFeedConversionRatios] = useState([]);
  // Chart options
  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Weight Distribution by Range",
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Animal Distribution by Type",
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Growth Projection",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Weight (kg)",
        },
      },
      x: {
        title: {
          display: true,
          text: "Days",
        },
      },
    },
  };

  // Fetch analytics data from backend
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch all analytics data in parallel
      const [
        summaryResponse,
        growthResponse,
        distributionResponse,
        feedResponse,
        fcrResponse,
      ] = await Promise.all([
        api.get(`/api/analytics/summary`),
        api.get(`/api/analytics/growth-rates?days=${timeRange}`),
        api.get(`/api/analytics/weight-distribution`),
        api.get(`/api/analytics/feed-consumption`, {
          params: dateRange.startDate && dateRange.endDate ? dateRange : {},
        }),
        api.get(`/api/analytics/feed-conversion-ratio`, {
          params: dateRange.startDate && dateRange.endDate ? dateRange : {},
        }),
      ]);

      setSummaryData(summaryResponse.data);
      setGrowthRates(growthResponse.data);

      // Transform weight distribution data for charts
      const transformedDistribution = transformDistributionData(
        distributionResponse.data,
      );
      setWeightDistribution(transformedDistribution);
      setFeedConsumption(feedResponse.data);
      setFeedConversionRatios(fcrResponse.data);
console.log("Growth Rates:", growthResponse.data);
setLoading(false);
    } catch (err) {
      setError("Failed to fetch analytics data");
      setLoading(false);
    }
  };

  // Transform distribution data for charting
  const transformDistributionData = (data) => {
    const ranges = ["0-20 kg", "21-40 kg", "41-60 kg", "61-80 kg", "81+ kg"];
    const types = [...new Set(data.map((item) => item.animal_type))];

    return ranges.map((range) => {
      const result = { name: range };
      types.forEach((type) => {
        const item = data.find(
          (d) => d.weight_range === range && d.animal_type === type,
        );
        result[type] = item ? item.count : 0;
      });
return result;
    });
  };

  // Prepare data for Chart.js
  const prepareChartData = () => {
    // Type distribution data for doughnut chart
    const typeLabels =
      summaryData.length > 0 ? summaryData.map((type) => type.animal_type) : [];
    const typeDataValues =
      summaryData.length > 0
        ? summaryData.map((type) => type.total_animals)
        : [];
    const typeBackgroundColors = ["#8884d8", "#82ca9d", "#ffc658", "#63e8ffff"];

    const typeData = {
      labels: typeLabels,
      datasets: [
        {
          label: "Number of Animals",
          data: typeDataValues,
          backgroundColor: typeBackgroundColors,
          borderColor: typeBackgroundColors.map((color) =>
            color.replace("0.6", "1"),
          ),
          borderWidth: 1,
        },
      ],
    };

    // Feed consumption data for bar chart
    const feedLabels = feedConsumption.map((item) => item.animal_type);
    const feedQuantityData = feedConsumption.map(
      (item) => item.total_quantity || 0,
    );
    const feedCostData = feedConsumption.map((item) => item.total_cost || 0);

    const feedQuantityDataChart = {
      labels: feedLabels,
      datasets: [
        {
          label: "Feed Quantity (kg)",
          data: feedQuantityData,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    };

    const feedCostDataChart = {
      labels: feedLabels,
      datasets: [
        {
          label: "Feed Cost",
          data: feedCostData,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
        },
      ],
    };

    // Feed conversion ratio data
    const fcrLabels = feedConversionRatios.map((item) => item.animal_type);
    const fcrData = feedConversionRatios.map(
      (item) => item.feed_conversion_ratio || 0,
    );

    const fcrChartData = {
      labels: fcrLabels,
      datasets: [
        {
          label: "Feed Conversion Ratio",
          data: fcrData,
          backgroundColor: "rgba(153, 102, 255, 0.6)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 1,
        },
      ],
    };

    // Weight distribution data for bar chart
    const weightLabels = weightDistribution.map((item) => item.name);
    const pigData = weightDistribution.map((item) => item.pigs || 0);
    const goatData = weightDistribution.map((item) => item.goats || 0);
    const sheepData = weightDistribution.map((item) => item.sheep || 0);
    const weanersData = weightDistribution.map((item) => item.weaners || 0);

    const weightData = {
      labels: weightLabels,
      datasets: [
        {
          label: "Pigs",
          data: pigData,
          backgroundColor: "rgba(136, 132, 216, 0.6)",
          borderColor: "rgba(136, 132, 216, 1)",
          borderWidth: 1,
        },
        {
          label: "Goats",
          data: goatData,
          backgroundColor: "rgba(130, 202, 157, 0.6)",
          borderColor: "rgba(130, 202, 157, 1)",
          borderWidth: 1,
        },
        {
          label: "Sheep",
          data: sheepData,
          backgroundColor: "rgba(255, 198, 88, 0.6)",
          borderColor: "rgba(255, 198, 88, 1)",
          borderWidth: 1,
        },
        {
          label: "Weaners",
          data: weanersData,
          backgroundColor: "rgba(88, 230, 255, 0.6)",
          borderColor: "rgba(88, 188, 255, 1)",
          borderWidth: 1,
        },
      ],
    };

    // Growth projection data (sample)
    const projectionLabels = [0, 30, 60, 90, 120];
    const projectionValues = [25, 35, 45, 55, 65];

    const projectionData = {
      labels: projectionLabels,
      datasets: [
        {
          label: "Projected Weight",
          data: projectionValues,
          borderColor: "rgb(75, 192, 192)",
          backgroundColor: "rgba(75, 192, 192, 0.5)",
          tension: 0.1,
        },
      ],
    };

    return {
      typeData,
      weightData,
      projectionData,
      feedQuantityDataChart,
      feedCostDataChart,
      fcrChartData,
    };
  };

  const {
    typeData,
    weightData,
    projectionData: chartProjectionData,
    feedQuantityDataChart,
    feedCostDataChart,
    fcrChartData,
  } = prepareChartData();

  // Calculate average growth rates by type
  const calculateAverageGrowthRates = () => {
    const ratesByType = {};
growthRates.forEach((item) => {
      if (!ratesByType[item.animal_type]) {
        ratesByType[item.animal_type] = {
          total: 0,
          count: 0,
          min: Infinity,
          max: -Infinity,
        };
      }

      ratesByType[item.animal_type].total += item.daily_gain;
      ratesByType[item.animal_type].count += 1;

      if (item.daily_gain < ratesByType[item.animal_type].min) {
        ratesByType[item.animal_type].min = item.daily_gain;
      }

      if (item.daily_gain > ratesByType[item.animal_type].max) {
        ratesByType[item.animal_type].max = item.daily_gain;
      }
    });

    // Calculate averages
    Object.keys(ratesByType).forEach((type) => {
      if (ratesByType[type].count > 0) {
        ratesByType[type].average =
          ratesByType[type].total / ratesByType[type].count;
      }
    });

    return ratesByType;
  };

  // Handle projection calculation
  const handleProjectionCalculation = async () => {
    if (!projectionInput.tagNumber.value || !projectionInput.targetWeight) {
      setError("Please provide both tag number and target weight");
      return;
    }

    try {
      const response = await api.get(`/api/analytics/projections`, {
        params: {
          tagNumber: projectionInput.tagNumber.value,
          animalType: projectionInput.animalType.value,
          targetWeight: parseFloat(projectionInput.targetWeight),
        },
      });

      setProjectionData(response.data);
    } catch (err) {
      setError(
        "Failed to calculate projection: " +
          (err.response?.data?.error || err.message),
      );
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  // Identify underperforming animals (growth rate below average)
  const identifyUnderperformers = () => {
    const averages = calculateAverageGrowthRates();
    return growthRates.filter((animal) => {
      const avgForType = averages[animal.animal_type]?.average || 0;
      return animal.daily_gain < avgForType * 0.7; // 30% below average
    });
  };

  const underperformingAnimals = identifyUnderperformers();

  // Generate recommendations based on data
  const generateRecommendations = () => {
    const recs = [];
    const averages = calculateAverageGrowthRates();

    // Check for underperforming animals
    if (underperformingAnimals.length > 0) {
      recs.push({
        type: "warning",
        text: `${underperformingAnimals.length} animals have growth rates significantly below average. Check their health and nutrition.`,
      });
    }

    // Check average growth rates by type
    Object.keys(averages).forEach((type) => {
      const expectedGain = type === "pigs" ? 0.7 : type === "goats" ? 0.3 : 0.4;
      if (averages[type].average < expectedGain * 0.8) {
        recs.push({
          type: "info",
          text: `Average growth rate for ${type} is below expected (${expectedGain} kg/day). Review feeding practices.`,
        });
      }
    });

    // Check for animals nearing market weight
    if (summaryData.length > 0) {
summaryData.forEach((type) => {
        const marketWeight =
          type.animal_type === "pigs"
            ? 80
            : type.animal_type === "goats"
            ? 35
            : 45;
        if (type.avg_weight > marketWeight * 0.8) {
          recs.push({
            type: "success",
            text: `${type.animal_type}s are approaching market weight. Consider sales planning.`,
          });
        }
      });
    }

    return recs;
  };

  const recommendations = generateRecommendations();

  // Columns for underperforming animals table
  const underperformerColumns = [
    {
      name: "Tag Number",
      selector: (row) => row.tagNumber,
      sortable: true,
    },
    {
      name: "Type",
      selector: (row) => row.animal_type,
      sortable: true,
      cell: (row) => (
        <Badge
          bg={
            row.animal_type === "pigs"
              ? "primary"
              : row.animal_type === "goats"
              ? "success"
              : "info"
          }
        >
          {row.animal_type}
        </Badge>
      ),
    },
    {
      name: "Daily Gain (kg)",
      selector: (row) => row?.daily_gain?.toFixed(3),
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => {
        const averages = calculateAverageGrowthRates();
        const avgForType = averages[row.animal_type]?.average || 0;
        const percentage = (row.daily_gain / avgForType) * 100;

        return (
          <Badge
            bg={
              percentage < 70
                ? "danger"
                : percentage < 90
                ? "warning"
                : "success"
            }
          >
            {percentage?.toFixed(1)}% of average
          </Badge>
        );
      },
    },
  ];

  // Animal type options
  const animalOptions = [
    { value: "pigs", label: "Pig" },
    { value: "goats", label: "Goat" },
    { value: "sheep", label: "Sheep" },
    { value: "weaners", label: "Weaners" },
  ];

  // Fetch tags when animalType changes
  useEffect(() => {
    const fetchTags = async () => {
      if (!projectionInput.animalType) return;

      setLoadingTags(true);
      try {
        const response = await api.get(
          `/api/${projectionInput?.animalType?.value}/available`,
        );
        // Convert to react-select options
        const tagOptions = response.data.map((t) => ({
          value: t.tagNumber,
          label: t.tagNumber,
        }));
        setTags(tagOptions);
      } catch (error) {
        console.error("Error fetching tags:", error);
        setTags([]);
      } finally {
        setLoadingTags(false);
      }
    };

    fetchTags();
  }, [projectionInput.animalType, apiUrl]);
if (loading)
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading analytics data...</span>
          </Spinner>
          <p className="mt-2">Loading analytics data...</p>
        </div>
      </Container>
    );

  // if (error)
  //   return (
  //     <Container fluid className="py-4">
  //       <Alert variant="danger">{error}</Alert>
  //     </Container>
  //   );

  return (
    <React.Fragment>
      
      {/* <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={setActiveTab}
        className="mb-4"
      >
        <Nav.Item>
          <Nav.Link eventKey="overview">Overview</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="performance">Performance Analysis</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="projections">Growth Projections</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="recommendations">Recommendations</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="feed">Feed Analytics</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="efficiency">Efficiency Metrics</Nav.Link>
        </Nav.Item>
      </Nav> */}

      {/* {activeTab === "overview" && (
        <Row>
          <Col md={4}>
            <Card className="mb-3">
              <Card.Header>Animal Distribution</Card.Header>
              <Card.Body>
                <div style={{ height: "300px" }}>
                  <Doughnut data={typeData} options={doughnutChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-3">
              <Card.Header>Weight Distribution</Card.Header>
              <Card.Body>
                <div style={{ height: "300px", width: "100%" }}>
                  <Bar data={weightData} options={barChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-3">
              <Card.Header>Feed Consumption by Type</Card.Header>
              <Card.Body>
                <div style={{ height: "300px" }}>
                  <Bar data={feedQuantityDataChart} options={barChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Performance Summary by Type</Card.Header>
              <Card.Body>
                {summaryData.map((type) => (
                  <div key={type.animal_type} className="mb-3">
                    <h6 className="text-capitalize">{type.animal_type}</h6>
                    <div className="d-flex justify-content-between">
                      <span>Count:</span>
                      <strong>{type.total_animals}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Avg Weight:</span>
                      <strong>{type?.avg_weight?.toFixed(1)} kg</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Weight Range:</span>
                      <strong>
                        {type?.min_weight?.toFixed(1)} -{" "}
                        {type?.max_weight?.toFixed(1)} kg
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Mortality Rate:</span>
                      <strong>
                        {(
                          (type.mortality_count / type.total_animals) *
                          100
                        )?.toFixed(1)}
                        %
                      </strong>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Growth Rates (Last {timeRange} Days)</Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Time Range:</Form.Label>
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                  >
                    <option value="30">30 Days</option>
                    <option value="60">60 Days</option>
                    <option value="90">90 Days</option>
                  </select>
                </Form.Group>

                {Object.entries(calculateAverageGrowthRates()).map(
                  ([type, data]) => (
                    <div key={type} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span className="text-capitalize">{type}:</span>
                        <span>
                          <strong>
                            {data?.average?.toFixed(3) || 0} kg/day
                          </strong>
                          <small className="text-muted ms-2">
                            ({data.min !== Infinity ? data?.min?.toFixed(3) : 0}{" "}
                            -{" "}
                            {data.max !== -Infinity ? data?.max?.toFixed(3) : 0}{" "}
                            kg/day)
                          </small>
                        </span>
                      </div>
                      <ProgressBar
                        now={data.average * 100}
                        max={type === "pigs" ? 100 : 50}
                        variant={
                          type === "pigs"
                            ? "primary"
                            : type === "goats"
                            ? "success"
                            : "info"
                        }
                        className="mb-2"
                      />
                    </div>
                  ),
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Summary Statistics</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <h6>Animal Counts</h6>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Animal Type</th>
                          <th>Count</th>
                          <th>Avg Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.map((item) => (
                          <tr key={item.animal_type}>
                            <td>{item.animal_type}</td>
                            <td>{item.total_animals}</td>
                            <td>
                              {item.avg_weight
                                ? item.avg_weight.toFixed(2) + " kg"
                                : "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>

                  <Col md={12}>
                    <h6>Feed Consumption</h6>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Animal Type</th>
                          <th>Quantity (kg)</th>
                          <th>Total Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feedConsumption.map((item) => (
                          <tr key={item.animal_type}>
                            <td>{item.animal_type}</td>
                            <td>{item.total_quantity || 0}</td>
                            <td>
                              Ksh{" "}
                              {item.total_cost ? item.total_cost.toFixed(2) : 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>

                  <Col md={12}>
                    <h6>Efficiency Metrics</h6>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Animal Type</th>
                          <th>FCR</th>
                          <th>Avg Weight Gain</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feedConversionRatios.map((item) => (
                          <tr key={item.animal_type}>
                            <td>{item.animal_type}</td>
                            <td>
                              {item.feed_conversion_ratio
                                ? item.feed_conversion_ratio.toFixed(2)
                                : "N/A"}
                            </td>
                            <td>
                              {item.avg_weight_gain
                                ? item.avg_weight_gain.toFixed(2) + " kg"
                                : "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )} */}

      {/* {activeTab === "performance" && (
        <Row>
          <Col md={12}>
            <Card className="mb-3">
              <Card.Header>
                Underperforming Animals
                <Badge bg="danger" className="ms-2">
                  {underperformingAnimals.length}
                </Badge>
              </Card.Header>
              <Card.Body>
                <DataTable
                  columns={underperformerColumns}
                  data={underperformingAnimals}
                  pagination
                  highlightOnHover
                />
              </Card.Body>
            </Card>
          </Col>

          <Col md={12}>
            <Card className="mb-3">
              <Card.Header>Growth Efficiency by Type</Card.Header>
              <Card.Body>
                <Row>
                  {Object.entries(calculateAverageGrowthRates()).map(
                    ([type, data]) => {
                      const expectedGain =
                        type === "pigs" ? 0.7 : type === "goats" ? 0.3 : 0.4;
                      const percentage = (data.average / expectedGain) * 100;
                      const variant =
                        percentage > 90
                          ? "success"
                          : percentage > 70
                          ? "warning"
                          : "danger";

                      return (
                        <Col md={4} key={type}>
                          <Card className="text-center">
                            <Card.Header className="text-capitalize">
                              {type}
                            </Card.Header>
                            <Card.Body>
                              <h4>{data.average?.toFixed(3) || 0} kg/day</h4>
                              <div
                                className={
                                  variant === "success"
                                    ? "text-success"
                                    : variant === "warning"
                                    ? "text-warning"
                                    : "text-danger"
                                }
                              >
                                {percentage?.toFixed(1)}% of target
                              </div>
                              <ProgressBar
                                now={percentage}
                                variant={variant}
                                className="mt-2"
                              />
                              <small className="text-muted">
                                Target: {expectedGain} kg/day
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    },
                  )}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )} */}

      {/* {activeTab === "projections" && (
        <Row>
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Weight Projection Calculator</Card.Header>
              <Card.Body>

                <Form.Group className="mb-3">
                  <Form.Label>Animal Type</Form.Label>
                  <Select
                    options={animalOptions}
                    value={projectionInput.animalType}
                    onChange={(selected) =>
                      setProjectionInput({
                        ...projectionInput,
                        animalType: selected,
                        tagNumber: null, // reset tag when type changes
                      })
                    }
                    placeholder="Select animal type..."
                    isClearable
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tag Number</Form.Label>
                  <Select
                    options={tags}
                    value={projectionInput.tagNumber}
                    onChange={(selected) =>
                      setProjectionInput({
                        ...projectionInput,
                        tagNumber: selected,
                      })
                    }
                    placeholder={
                      loadingTags
                        ? "Loading tags..."
                        : !projectionInput.animalType
                        ? "Select animal type first"
                        : "Select tag number..."
                    }
                    isDisabled={!projectionInput.animalType || loadingTags}
                    isClearable
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Target Weight (kg)</Form.Label>
                  <Form.Control
                    type="number"
                    value={projectionInput.targetWeight}
                    onChange={(e) =>
                      setProjectionInput({
                        ...projectionInput,
                        targetWeight: e.target.value,
                      })
                    }
                    placeholder="Enter target weight"
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  onClick={handleProjectionCalculation}
                  disabled={
                    !projectionInput.tagNumber || !projectionInput.targetWeight
                  }
                >
                  Calculate Projection
                </Button>

                {projectionData && (
                  <div className="mt-3">
                    {projectionData.error ? (
                      <Alert variant="warning">{projectionData.error}</Alert>
                    ) : (
                      <>
                        <h6>Projection Results:</h6>
                        <div className="d-flex justify-content-between">
                          <span>Current Weight:</span>
                          <strong>{projectionData.currentWeight} kg</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Daily Gain:</span>
                          <strong>
                            {projectionData?.dailyGain?.toFixed(3)} kg/day
                          </strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Days to Target:</span>
                          <strong>
                            {projectionData.daysToTarget || "N/A"}
                          </strong>
                        </div>
                        {projectionData.projectedDate && (
                          <div className="d-flex justify-content-between">
                            <span>Projected Date:</span>
                            <strong>{projectionData.projectedDate}</strong>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Growth Projection</Card.Header>
              <Card.Body>
                <div style={{ height: "300px" }}>
                  <Line data={chartProjectionData} options={lineChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )} */}

      {/* {activeTab === "recommendations" && (
        <Row>
          <Col md={12}>
            <Card className="mb-3">
              <Card.Header>Actionable Recommendations</Card.Header>
              <Card.Body>
                {recommendations.length > 0 ? (
                  recommendations.map((rec, index) => (
                    <Alert
                      key={index}
                      variant={rec.type}
                      className="d-flex align-items-center"
                    >
                      <div className="me-3">
                        {rec.type === "success"
                          ? "✅"
                          : rec.type === "warning"
                          ? "⚠️"
                          : "ℹ️"}
                      </div>
                      <div>{rec.text}</div>
                    </Alert>
                  ))
                ) : (
                  <Alert variant="success">
                    🎉 All animals are performing within expected parameters.
                    Keep up the good management practices!
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Feed Optimization</Card.Header>
              <Card.Body>
                <h5>Suggested Adjustments:</h5>
                <ul>
                  <li>
                    Increase protein supplement for underperforming animals by
                    15%
                  </li>
                  <li>
                    Consider adding growth promoters for pigs with growth rates
                    below 0.6 kg/day
                  </li>
                  <li>Adjust feeding schedule to 3x daily for faster growth</li>
                  <li>
                    Review feed quality for animals with inconsistent growth
                    patterns
                  </li>
                </ul>
                <Button variant="outline-primary" size="sm">
                  View Detailed Feeding Plan
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Health Management</Card.Header>
              <Card.Body>
                <h5>Preventive Measures:</h5>
                <ul>
                  <li>Schedule deworming for all animals next week</li>
                  <li>
                    Check water quality in pens with multiple underperformers
                  </li>
                  <li>
                    Isolate and monitor animals with growth rates below 50% of
                    average
                  </li>
                  <li>
                    Review vaccination schedule for animals with health issues
                  </li>
                </ul>
                <Button variant="outline-primary" size="sm">
                  View Health Schedule
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )} */}

      {/* {activeTab === "feed" && (
        <Row>
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Feed Quantity Consumption</Card.Header>
              <Card.Body>
                <div style={{ height: "300px" }}>
                  <Bar data={feedQuantityDataChart} options={barChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Feed Cost Analysis</Card.Header>
              <Card.Body>
                <div style={{ height: "300px" }}>
                  <Bar data={feedCostDataChart} options={barChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={12}>
            <Card className="mb-3">
              <Card.Header>Detailed Feed Consumption</Card.Header>
              <Card.Body>
                <Table striped bordered responsive>
                  <thead>
                    <tr>
                      <th>Animal Type</th>
                      <th>Total Quantity (kg)</th>
                      <th>Total Cost</th>
                      <th>Records Count</th>
                      <th>Avg Daily Consumption</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedConsumption.map((item) => (
                      <tr key={item.animal_type}>
                        <td>{item.animal_type}</td>
                        <td>{item.total_quantity || 0}</td>
                        <td>
                          Ksh {item.total_cost ? item.total_cost.toFixed(2) : 0}
                        </td>
                        <td>{item.records_count || 0}</td>
                        <td>
                          {item.total_quantity && item.records_count
                            ? (
                                item.total_quantity / item.records_count
                              ).toFixed(2) + " kg/day"
                            : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )} */}

      {/* {activeTab === "efficiency" && (
        <Row>
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Feed Conversion Ratio (FCR)</Card.Header>
              <Card.Body>
                <div style={{ height: "300px" }}>
                  <Bar data={fcrChartData} options={barChartOptions} />
                </div>
                <p className="mt-2 text-muted">
                  <small>
                    FCR = Total feed consumed / Total weight gain. Lower values
                    indicate better efficiency.
                  </small>
                </p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-3">
              <Card.Header>Efficiency Analysis</Card.Header>
              <Card.Body>
                <h6>Interpretation Guide</h6>
                <ul>
                  <li>FCR &lt; 3.0: Excellent efficiency</li>
                  <li>FCR 3.0-4.0: Good efficiency</li>
                  <li>FCR 4.0-5.0: Average efficiency</li>
                  <li>FCR &gt; 5.0: Needs improvement</li>
                </ul>

                <h6 className="mt-3">Recommendations</h6>
                {feedConversionRatios.map((item) => {
                  let recommendation = "";
                  if (item.feed_conversion_ratio < 3) {
                    recommendation =
                      "Excellent efficiency. Maintain current practices.";
                  } else if (item.feed_conversion_ratio < 4) {
                    recommendation =
                      "Good efficiency. Consider minor adjustments to feed composition.";
                  } else if (item.feed_conversion_ratio < 5) {
                    recommendation =
                      "Average efficiency. Review feeding schedules and diet composition.";
                  } else {
                    recommendation =
                      "Needs improvement. Evaluate feed quality, animal health, and feeding practices.";
                  }

                  return (
                    <div key={item.animal_type} className="mb-2">
                      <strong>{item.animal_type}:</strong> {recommendation}
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>

          <Col md={12}>
            <Card className="mb-3">
              <Card.Header>Cost Analysis</Card.Header>
              <Card.Body>
                <Row>
                  {feedConsumption.map((item) => {
                    const fcr = feedConversionRatios.find(
                      (f) => f.animal_type === item.animal_type,
                    )?.feed_conversion_ratio;
                    const costPerKgGain =
                      fcr && item.total_cost
                        ? (
                            item.total_cost /
                            (item.total_quantity / fcr)
                          ).toFixed(2)
                        : "N/A";

                    return (
                      <Col md={3} key={item.animal_type}>
                        <Card className="text-center mb-3">
                          <Card.Header className="text-capitalize">
                            {item.animal_type}
                          </Card.Header>
                          <Card.Body>
                            <h6>Cost per kg Weight Gain</h6>
                            <h5
                              className={
                                costPerKgGain < 5
                                  ? "text-success"
                                  : costPerKgGain < 8
                                  ? "text-warning"
                                  : "text-danger"
                              }
                            >
                              Ksh {costPerKgGain}
                            </h5>
                            <small className="text-muted">
                              Based on {item.total_quantity || 0} kg feed
                              consumed
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )} */}

      <SlaughterWaste/>
    </React.Fragment>
  );
};

export default AnimalAnalyticsDashboard;
